import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Truck, Award } from 'lucide-react';
import { 
  EditModeProvider, 
  EditModeContext, 
  AdminToolbar, 
  EditableText, 
  EditableImage 
} from '../components/EditableContent';
import SEO, { OrganizationSchema, LocalBusinessSchema } from '../components/SEO';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HomePageContent() {
  const { pageContent, setPageContent, editMode } = useContext(EditModeContext);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, prodRes, projRes, imgRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products?limit=8`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/site-images`).catch(() => ({ data: {} })),
      ]);
      const vendorRes = await axios.get(`${API}/vendors`).catch(() => ({ data: { vendors: [] } }));
      setCategories(catRes.data);
      setProducts(prodRes.data.products || []);
      setProjects(projRes.data.slice(0, 2));
      setSiteImages(imgRes.data || {});
      setVendors(vendorRes.data.vendors || []);
    } catch (err) {
      console.error('Error fetching home data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get block content by type
  const getBlockContent = (type, defaultContent = {}) => {
    const block = pageContent?.blocks?.find(b => b.type === type);
    return block?.content || defaultContent;
  };

  // Helper to update block content
  const updateBlockContent = (type, updates) => {
    if (!pageContent) return;
    const blockIndex = pageContent.blocks?.findIndex(b => b.type === type);
    if (blockIndex >= 0) {
      const newBlocks = [...pageContent.blocks];
      newBlocks[blockIndex] = { 
        ...newBlocks[blockIndex], 
        content: { ...newBlocks[blockIndex].content, ...updates } 
      };
      setPageContent({ ...pageContent, blocks: newBlocks });
    }
  };

  // Get content for each section
  const heroContent = getBlockContent('hero', {
    title: 'Industrial Technology',
    title_accent: 'Solutions',
    subtitle: "Australia's trusted distributor for networking, solar controllers, power systems, and outdoor enclosures. Trade pricing available.",
    cta_text: 'SHOP NOW',
    cta_link: '/products',
    background_image: '',
  });

  const featuresContent = getBlockContent('features', {
    items: [
      { icon: 'truck', title: 'Australia-Wide Shipping', subtitle: 'Fast delivery to all states' },
      { icon: 'shield', title: 'Trade Pricing', subtitle: 'Exclusive rates for approved accounts' },
      { icon: 'award', title: 'Quality Guaranteed', subtitle: 'Premium industrial-grade products' },
    ]
  });

  const categoriesContent = getBlockContent('category_grid', {
    title: 'Browse Categories',
    subtitle: 'Find the right products for your project',
  });

  const productsContent = getBlockContent('featured_products', {
    title: 'Featured Products',
    subtitle: 'Popular items from our catalog',
  });

  const tradeContent = getBlockContent('trade_cta', {
    title: 'Trade Pricing',
    description: 'Registered trade customers receive exclusive pricing. Apply for a trade account today and unlock better margins for your business.',
    cta_text: 'APPLY NOW',
    cta_link: '/trade-application',
  });

  const projectsContent = getBlockContent('projects', {
    title: 'Customer Projects',
    subtitle: 'See how our products are used in the field',
  });

  const vendorsContent = getBlockContent('vendor_showcase', {
    title: 'Our Vendor Partners',
    subtitle: 'Industry-leading brands we proudly distribute',
    layout: 'cards',
  });

  // Hero image carousel
  const heroImages = (() => {
    const imgs = siteImages.homepage_hero;
    if (!imgs) return [];
    const urls = imgs.urls?.length ? imgs.urls : (imgs.url ? [imgs.url] : []);
    return urls.map(u => u.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u).filter(Boolean);
  })();
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroReady, setHeroReady] = useState(false);
  const heroTimerRef = useRef(null);

  useEffect(() => {
    if (heroImages.length === 0) return;
    // Small delay before enabling transitions to prevent initial flicker
    const readyTimer = setTimeout(() => setHeroReady(true), 100);
    if (heroImages.length <= 1) return () => clearTimeout(readyTimer);
    heroTimerRef.current = setInterval(() => {
      setHeroIndex(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => { clearTimeout(readyTimer); clearInterval(heroTimerRef.current); };
  }, [heroImages.length]);

  return (
    <Layout>
      <SEO 
        title="Industrial Technology Solutions"
        description="Australia's trusted distributor for networking equipment, solar controllers, power systems, and outdoor enclosures. Trade pricing available."
        url="/"
      />
      <OrganizationSchema />
      <LocalBusinessSchema />
      
      {/* Hero Section with Crossfade */}
      <section 
        className="relative min-h-[70vh] flex items-center mesh-bg mesh-overlay" 
        data-testid="hero-section"
      >
        {/* Background images with crossfade */}
        {heroImages.length > 0 ? heroImages.map((url, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 bg-cover bg-center ${heroReady ? 'transition-opacity duration-1000 ease-in-out' : ''}`}
            style={{
              backgroundImage: `url(${url})`,
              opacity: idx === heroIndex ? 1 : 0,
              zIndex: 1,
            }}
            data-testid={`hero-bg-${idx}`}
          />
        )) : heroContent.background_image ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroContent.background_image})`, zIndex: 1 }}
          />
        ) : null}
        {/* Overlay for readability */}
        {heroImages.length > 0 && (
          <div className="absolute inset-0 bg-black/50" style={{ zIndex: 2 }} />
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl animate-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight mb-6">
              <EditableText
                value={heroContent.title}
                onChange={(val) => updateBlockContent('hero', { title: val })}
                tag="span"
                className="block"
                placeholder="Main Title"
              />
              <EditableText
                value={heroContent.title_accent || 'Solutions'}
                onChange={(val) => updateBlockContent('hero', { title_accent: val })}
                tag="span"
                className="block text-[#78BE20]"
                placeholder="Accent Title"
              />
            </h1>
            <EditableText
              value={heroContent.subtitle}
              onChange={(val) => updateBlockContent('hero', { subtitle: val })}
              tag="p"
              className="text-lg sm:text-xl text-zinc-300 mb-8 max-w-2xl"
              placeholder="Subtitle text..."
              multiline
            />
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={heroContent.cta_link || '/products'}>
                <Button className="btn-primary text-base" data-testid="shop-now-btn">
                  {heroContent.cta_text || 'SHOP NOW'} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/trade-application">
                <Button variant="outline" className="btn-secondary text-base" data-testid="trade-apply-btn">
                  APPLY FOR TRADE
                </Button>
              </Link>
            </div>
          </div>
        </div>
        {/* Slide indicator dots */}
        {heroImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2" style={{ zIndex: 10 }} data-testid="hero-dots">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setHeroIndex(idx);
                  clearInterval(heroTimerRef.current);
                  heroTimerRef.current = setInterval(() => {
                    setHeroIndex(prev => (prev + 1) % heroImages.length);
                  }, 5000);
                }}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === heroIndex ? 'bg-[#78BE20] scale-110' : 'bg-white/40 hover:bg-white/60'
                }`}
                data-testid={`hero-dot-${idx}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Features */}
      <section className="bg-zinc-900 border-y border-zinc-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                <Truck className="h-6 w-6 text-[#78BE20]" />
              </div>
              <div>
                <EditableText
                  value={featuresContent.items?.[0]?.title || 'Australia-Wide Shipping'}
                  onChange={(val) => {
                    const items = [...(featuresContent.items || [])];
                    items[0] = { ...items[0], title: val };
                    updateBlockContent('features', { items });
                  }}
                  tag="h3"
                  className="text-white font-bold"
                />
                <EditableText
                  value={featuresContent.items?.[0]?.subtitle || 'Fast delivery to all states'}
                  onChange={(val) => {
                    const items = [...(featuresContent.items || [])];
                    items[0] = { ...items[0], subtitle: val };
                    updateBlockContent('features', { items });
                  }}
                  tag="p"
                  className="text-zinc-400 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                <Shield className="h-6 w-6 text-[#78BE20]" />
              </div>
              <div>
                <EditableText
                  value={featuresContent.items?.[1]?.title || 'Trade Pricing'}
                  onChange={(val) => {
                    const items = [...(featuresContent.items || [])];
                    items[1] = { ...items[1], title: val };
                    updateBlockContent('features', { items });
                  }}
                  tag="h3"
                  className="text-white font-bold"
                />
                <EditableText
                  value={featuresContent.items?.[1]?.subtitle || 'Exclusive rates for approved accounts'}
                  onChange={(val) => {
                    const items = [...(featuresContent.items || [])];
                    items[1] = { ...items[1], subtitle: val };
                    updateBlockContent('features', { items });
                  }}
                  tag="p"
                  className="text-zinc-400 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#78BE20]/10 rounded-sm">
                <Award className="h-6 w-6 text-[#78BE20]" />
              </div>
              <div>
                <EditableText
                  value={featuresContent.items?.[2]?.title || 'Quality Guaranteed'}
                  onChange={(val) => {
                    const items = [...(featuresContent.items || [])];
                    items[2] = { ...items[2], title: val };
                    updateBlockContent('features', { items });
                  }}
                  tag="h3"
                  className="text-white font-bold"
                />
                <EditableText
                  value={featuresContent.items?.[2]?.subtitle || 'Premium industrial-grade products'}
                  onChange={(val) => {
                    const items = [...(featuresContent.items || [])];
                    items[2] = { ...items[2], subtitle: val };
                    updateBlockContent('features', { items });
                  }}
                  tag="p"
                  className="text-zinc-400 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-background" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <EditableText
                value={categoriesContent.title || 'Browse Categories'}
                onChange={(val) => updateBlockContent('category_grid', { title: val })}
                tag="h2"
                className="text-2xl sm:text-3xl font-black text-foreground uppercase tracking-tight"
              />
              <EditableText
                value={categoriesContent.subtitle || 'Find the right products for your project'}
                onChange={(val) => updateBlockContent('category_grid', { subtitle: val })}
                tag="p"
                className="text-muted-foreground mt-2"
              />
            </div>
            <Link to="/products" className="hidden sm:flex items-center text-[#78BE20] hover:text-[#65a31b] font-medium transition-colors">
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat, idx) => (
              <Link
                key={cat.id}
                to={`/products?category=${cat.slug}`}
                className={`animate-fade-up stagger-${idx + 1}`}
                data-testid={`category-${cat.slug}`}
              >
                <div className="bg-card border border-border rounded-sm overflow-hidden card-hover group aspect-square relative">
                  {cat.image && (
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-sm sm:text-base group-hover:text-[#78BE20] transition-colors">
                      {cat.name}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-zinc-900/50" data-testid="featured-products">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <EditableText
                value={productsContent.title || 'Featured Products'}
                onChange={(val) => updateBlockContent('featured_products', { title: val })}
                tag="h2"
                className="text-2xl sm:text-3xl font-black text-foreground uppercase tracking-tight"
              />
              <EditableText
                value={productsContent.subtitle || 'Popular items from our catalog'}
                onChange={(val) => updateBlockContent('featured_products', { subtitle: val })}
                tag="p"
                className="text-muted-foreground mt-2"
              />
            </div>
            <Link to="/products" className="hidden sm:flex items-center text-[#78BE20] hover:text-[#65a31b] font-medium transition-colors">
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-sm animate-pulse">
                  <div className="aspect-square bg-zinc-800" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-zinc-800 rounded w-1/4" />
                    <div className="h-5 bg-zinc-800 rounded w-3/4" />
                    <div className="h-6 bg-zinc-800 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trade CTA */}
      {vendors.length > 0 && (
        <section className="py-16 bg-background" data-testid="vendor-showcase">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-8">
              <div>
                <EditableText
                  value={vendorsContent.title || 'Our Vendor Partners'}
                  onChange={(val) => updateBlockContent('vendor_showcase', { title: val })}
                  tag="h2"
                  className="text-2xl sm:text-3xl font-black text-foreground uppercase tracking-tight"
                />
                <EditableText
                  value={vendorsContent.subtitle || 'Industry-leading brands we proudly distribute'}
                  onChange={(val) => updateBlockContent('vendor_showcase', { subtitle: val })}
                  tag="p"
                  className="text-muted-foreground mt-2"
                />
              </div>
              <Link to="/vendors" className="hidden sm:flex items-center text-[#78BE20] hover:text-[#65a31b] font-medium transition-colors">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vendors.filter(v => v.enabled !== false).map(vendor => (
                <Link key={vendor.id} to={`/vendors/${vendor.slug}`} data-testid={`vendor-card-${vendor.slug}`}>
                  <div className="bg-card border border-border rounded-sm p-6 card-hover group h-full">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-bold text-white group-hover:text-[#78BE20] transition-colors">{vendor.name}</h3>
                      {vendor.logo_url && (
                        <div className="h-7 max-w-[120px] shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                          <img
                            src={vendor.logo_url}
                            alt={vendor.name}
                            className="h-full w-auto object-contain"
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                    <p className="text-zinc-400 text-sm line-clamp-2">{vendor.description}</p>
                    <div className="flex items-center gap-3 mt-4">
                      {vendor.highlights?.slice(0, 3).map((h, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-zinc-800 text-zinc-300 rounded-sm">
                          {h.value} {h.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trade CTA -- original */}
      <section className="py-16 bg-[#78BE20]" data-testid="trade-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <EditableText
                value={tradeContent.title || 'Trade Pricing'}
                onChange={(val) => updateBlockContent('trade_cta', { title: val })}
                tag="h2"
                className="text-2xl sm:text-3xl font-black text-black uppercase tracking-tight"
              />
              <EditableText
                value={tradeContent.description || 'Registered trade customers receive exclusive pricing. Apply for a trade account today and unlock better margins for your business.'}
                onChange={(val) => updateBlockContent('trade_cta', { description: val })}
                tag="p"
                className="text-black/70 mt-2 max-w-xl"
                multiline
              />
            </div>
            <Link to={tradeContent.cta_link || '/trade-application'}>
              <Button className="bg-black text-white hover:bg-zinc-800 text-base px-8 py-6" data-testid="trade-cta-btn">
                {tradeContent.cta_text || 'APPLY NOW'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Projects */}
      {projects.length > 0 && (
        <section className="py-16 bg-background" data-testid="projects-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-8">
              <div>
                <EditableText
                  value={projectsContent.title || 'Customer Projects'}
                  onChange={(val) => updateBlockContent('projects', { title: val })}
                  tag="h2"
                  className="text-2xl sm:text-3xl font-black text-foreground uppercase tracking-tight"
                />
                <EditableText
                  value={projectsContent.subtitle || 'See how our products are used in the field'}
                  onChange={(val) => updateBlockContent('projects', { subtitle: val })}
                  tag="p"
                  className="text-muted-foreground mt-2"
                />
              </div>
              <Link to="/projects" className="hidden sm:flex items-center text-[#78BE20] hover:text-[#65a31b] font-medium transition-colors">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.map(project => (
                <Link key={project.id} to={`/projects/${project.slug}`}>
                  <div className="bg-card border border-border rounded-sm overflow-hidden card-hover group">
                    {project.gallery?.[0] && (
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={project.gallery[0]}
                          alt={project.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {project.tags?.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs px-2 py-1 bg-[#78BE20]/10 text-[#78BE20] rounded-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-xl font-bold text-foreground group-hover:text-[#78BE20] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-muted-foreground mt-2 line-clamp-2">{project.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Additional CMS Blocks */}
      <CMSBlockRenderer excludeTypes={['hero', 'features', 'category_grid', 'featured_products', 'vendor_showcase', 'trade_cta', 'projects']} />

      {/* Admin Toolbar */}
      <AdminToolbar />
    </Layout>
  );
}

export default function HomePage() {
  return (
    <EditModeProvider pageSlug="home">
      <HomePageContent />
    </EditModeProvider>
  );
}
